
import type { AuditReport, CategoryScore } from '../../types/audit';

// Inform TypeScript about the global variables from the CDN scripts
declare const jspdf: any;

const COLORS = {
  primary: [79, 70, 229], // #4F46E5 (Indigo 600)
  primaryLight: [238, 242, 255], // Indigo 50
  textMain: [31, 41, 55], // Gray 800
  textLight: [107, 114, 128], // Gray 500
  success: [16, 185, 129], // Green 500
  warning: [245, 158, 11], // Amber 500
  danger: [239, 68, 68], // Red 500
  white: [255, 255, 255],
  greyBg: [249, 250, 251] // Gray 50
};

const CATEGORY_THEMES: Record<string, { label: string, color: number[], icon: string }> = {
  linguistics: { label: "Linguistic Efficiency", color: [239, 246, 255], icon: "Book" }, // Blue 50
  phonetics: { label: "Phonetic Clarity", color: [240, 253, 244], icon: "Mic" }, // Green 50
  digital: { label: "Digital Readiness", color: [245, 243, 255], icon: "Laptop" }, // Violet 50
  structural: { label: "Structural Integrity", color: [255, 251, 235], icon: "Scale" }, // Amber 50
  strategic: { label: "Strategic Intent", color: [254, 242, 242], icon: "Target" } // Red 50
};

// Helper to fetch image
const getImageAsBase64 = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob'; 
    xhr.onload = function () {
      if (this.status === 200) {
        const blob = this.response;
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      } else {
        // Resolve empty to fail gracefully
        resolve('');
      }
    };
    xhr.onerror = () => resolve(''); 
    xhr.send();
  });
};

// Helper to draw text and wrap it
const addWrappedText = (doc: any, text: string, x: number, y: number, maxWidth: number, fontSize: number, color: number[], fontStyle: 'normal' | 'bold' | 'italic' = 'normal', fontName: 'helvetica' | 'times' = 'helvetica') => {
  doc.setFontSize(fontSize);
  doc.setTextColor(...color);
  doc.setFont(fontName, fontStyle);
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return lines.length * (fontSize * 0.5) + (lines.length * 2); // approximate height with line spacing
};

// Draw Footer
const drawFooter = (doc: any, pageNumber: number, totalPages: number) => {
    const width = doc.internal.pageSize.getWidth();
    const height = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175); // Gray 400
    doc.setFont('helvetica', 'normal');
    const text = `Report generated by BrandGenius | brandgenius.app`;
    doc.text(text, width / 2, height - 15, { align: 'center' });
};

// Draw Background Watermark
const drawWatermark = (doc: any) => {
    // Since jsPDF doesn't support transparency well without plugins, we skip heavy watermarks
    // or draw very light grey text if needed. For now, we'll keep it clean.
};

// Helper to draw the radar chart manually in PDF vectors for high quality
const drawRadarChart = (doc: any, report: AuditReport, centerX: number, centerY: number, radius: number) => {
  const scores = [
    report.categories.linguistics.score,
    report.categories.phonetics.score,
    report.categories.digital.score,
    report.categories.structural.score,
    report.categories.strategic.score
  ];
  const labels = ["Linguistics", "Phonetics", "Digital", "Structure", "Strategy"];
  const numPoints = 5;
  const angleStep = (Math.PI * 2) / numPoints;
  const startAngle = -Math.PI / 2; // Start at top

  // Draw background grid (webs)
  doc.setDrawColor(229, 231, 235); // Gray 200
  doc.setLineWidth(0.5);
  
  [0.2, 0.4, 0.6, 0.8, 1].forEach(scale => {
    for (let i = 0; i < numPoints; i++) {
      const angle1 = startAngle + i * angleStep;
      const angle2 = startAngle + ((i + 1) % numPoints) * angleStep;
      const x1 = centerX + Math.cos(angle1) * radius * scale;
      const y1 = centerY + Math.sin(angle1) * radius * scale;
      const x2 = centerX + Math.cos(angle2) * radius * scale;
      const y2 = centerY + Math.sin(angle2) * radius * scale;
      doc.line(x1, y1, x2, y2);
    }
  });

  // Draw axes
  for (let i = 0; i < numPoints; i++) {
    const angle = startAngle + i * angleStep;
    const x = centerX + Math.cos(angle) * radius;
    const y = centerY + Math.sin(angle) * radius;
    doc.line(centerX, centerY, x, y);
    
    // Labels
    const labelX = centerX + Math.cos(angle) * (radius + 15);
    const labelY = centerY + Math.sin(angle) * (radius + 15);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textLight);
    doc.setFont('helvetica', 'bold');
    doc.text(labels[i].toUpperCase(), labelX, labelY, { align: 'center', baseline: 'middle' });
  }

  // Draw Data Polygon
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(1.5);
  // Simulating fill with lines (hatching) could be done, but solid outline is safer for basic jsPDF
  
  const points: {x: number, y: number}[] = [];
  
  for (let i = 0; i < numPoints; i++) {
    const angle = startAngle + i * angleStep;
    const value = Math.max(0, Math.min(100, scores[i])) / 100;
    const x = centerX + Math.cos(angle) * radius * value;
    const y = centerY + Math.sin(angle) * radius * value;
    points.push({x, y});
  }

  // Close loop
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    doc.line(p1.x, p1.y, p2.x, p2.y);
    
    // Dot
    doc.setFillColor(...COLORS.primary);
    doc.circle(p1.x, p1.y, 2, 'F');
  }
};

const drawCategoryPage = (doc: any, key: string, data: CategoryScore, pageWidth: number, pageHeight: number, margin: number) => {
  doc.addPage();
  const theme = CATEGORY_THEMES[key];
  
  // Background Tint
  doc.setFillColor(...theme.color);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // White Container
  doc.setFillColor(...COLORS.white);
  doc.roundedRect(margin, margin, pageWidth - (margin * 2), pageHeight - (margin * 2), 5, 5, 'F');
  
  let currentY = margin + 20;
  
  // Header
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.textLight);
  doc.setFont('helvetica', 'bold');
  doc.text("CATEGORY DEEP DIVE", margin + 15, currentY);
  
  currentY += 15;
  
  // Title
  doc.setFontSize(28);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('times', 'bold');
  doc.text(theme.label, margin + 15, currentY);
  
  // Score Badge
  const scoreX = pageWidth - margin - 60;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.setFillColor(...COLORS.primaryLight);
  doc.roundedRect(scoreX, margin + 15, 45, 25, 3, 3, 'FD');
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`${data.score}/100`, scoreX + 22.5, margin + 28, { align: 'center' });
  doc.setFontSize(8);
  doc.text(data.grade, scoreX + 22.5, margin + 35, { align: 'center' });
  
  currentY += 30;
  
  // Risk Level
  const riskColor = data.riskLevel === 'Low' ? COLORS.success : data.riskLevel === 'Medium' ? COLORS.warning : COLORS.danger;
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.textMain);
  doc.setFont('helvetica', 'bold');
  doc.text("Risk Level:", margin + 15, currentY);
  doc.setTextColor(...riskColor);
  doc.text(data.riskLevel.toUpperCase(), margin + 45, currentY);
  
  currentY += 20;
  
  // Why This Matters (Box)
  doc.setFillColor(...COLORS.greyBg);
  doc.setDrawColor(229, 231, 235);
  doc.roundedRect(margin + 15, currentY, pageWidth - (margin * 2) - 30, 45, 2, 2, 'FD');
  
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.textMain);
  doc.setFont('times', 'bold');
  doc.text("Why This Matters", margin + 25, currentY + 12);
  
  const whyHeight = addWrappedText(doc, data.whyItMatters, margin + 25, currentY + 25, pageWidth - (margin * 2) - 50, 10, COLORS.textLight, 'normal', 'helvetica');
  
  currentY += 60;
  
  // Analysis (Strengths & Risks)
  const leftColX = margin + 15;
  const rightColX = pageWidth / 2 + 5;
  const colWidth = (pageWidth - (margin * 2) - 40) / 2;
  
  const startAnalysisY = currentY;
  
  // Strengths
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.success);
  doc.setFont('times', 'bold');
  doc.text("Strengths", leftColX, currentY);
  currentY += 10;
  
  if (data.strengths.length > 0) {
      data.strengths.forEach(s => {
          doc.setTextColor(...COLORS.textMain);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          const lines = doc.splitTextToSize(`• ${s}`, colWidth);
          doc.text(lines, leftColX, currentY);
          currentY += (lines.length * 5) + 3;
      });
  } else {
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.textLight);
      doc.text("No specific strengths detected.", leftColX, currentY);
      currentY += 10;
  }
  
  // Reset Y for Right Column (Risks)
  let rightY = startAnalysisY;
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.danger);
  doc.setFont('times', 'bold');
  doc.text("Risks & Issues", rightColX, rightY);
  rightY += 10;
  
  if (data.issues.length > 0) {
      data.issues.forEach(i => {
          doc.setTextColor(...COLORS.textMain);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          const lines = doc.splitTextToSize(`• ${i}`, colWidth);
          doc.text(lines, rightColX, rightY);
          rightY += (lines.length * 5) + 3;
      });
  } else {
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.textLight);
      doc.text("No critical issues detected.", rightColX, rightY);
      rightY += 10;
  }
  
  currentY = Math.max(currentY, rightY) + 20;
  
  // Consultant Guidance (Highlighted)
  doc.setFillColor(...COLORS.primaryLight);
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin + 15, currentY, pageWidth - (margin * 2) - 30, 50, 2, 2, 'FD');
  
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('times', 'bold');
  doc.text("Consultant Recommendation", margin + 25, currentY + 12);
  
  addWrappedText(doc, data.guidance, margin + 25, currentY + 25, pageWidth - (margin * 2) - 50, 10, COLORS.textMain, 'italic', 'helvetica');
  
  currentY += 60;
  
  // Benchmark
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.textLight);
  doc.setFont('helvetica', 'bold');
  doc.text("BENCHMARK INSIGHT:", margin + 15, currentY);
  doc.setFont('helvetica', 'normal');
  doc.text(data.benchmark, margin + 65, currentY);
};

export const exportAuditToPdf = async (report: AuditReport, customLogoBase64?: string) => {
  const { jsPDF } = jspdf;
  const doc = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4'
  });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const margin = 15;

  // Load Logo asynchronously
  let logoBase64 = '';
  try {
      if (customLogoBase64) {
          logoBase64 = customLogoBase64;
      } else {
          logoBase64 = await getImageAsBase64('https://www.brandgenius.app/brand-name-generator-logo.png');
      }
  } catch (e) {
      console.warn('Logo load failed', e);
  }

  // --- PAGE 1: COVER ---
  
  // Logo
  if (logoBase64) {
      // Different logos have different aspect ratios, so we bound the max width/height
      const maxLogoWidth = 50;
      const maxLogoHeight = 15;
      doc.addImage(logoBase64, 'PNG', margin, margin, maxLogoWidth, maxLogoHeight, undefined, 'FAST');
  } else {
      doc.setFontSize(18);
      doc.setTextColor(...COLORS.primary);
      doc.setFont('times', 'bold');
      doc.text("BrandGenius", margin, margin + 10);
  }
  
  // Divider
  doc.setDrawColor(209, 213, 219); // Gray 300
  doc.line(margin, margin + 15, width - margin, margin + 15);
  
  // Main Cover Content
  const coverCenterY = height / 2.5;
  
  // Brand Name
  doc.setFontSize(48);
  doc.setTextColor(...COLORS.textMain);
  doc.setFont('times', 'bold');
  doc.text(report.inputs.name, width / 2, coverCenterY - 30, { align: 'center' });
  
  // Subtitle
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.textLight);
  doc.setFont('helvetica', 'normal');
  doc.text("COMPREHENSIVE BRAND AUDIT REPORT", width / 2, coverCenterY - 15, { align: 'center' });
  
  // Brand Type Tag
  doc.setFillColor(...COLORS.primaryLight);
  doc.roundedRect((width / 2) - 30, coverCenterY, 60, 10, 5, 5, 'F');
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text(report.brandType.toUpperCase(), width / 2, coverCenterY + 6.5, { align: 'center' });
  
  // Grade Circle
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(2);
  doc.circle(width / 2, coverCenterY + 40, 20, 'S');
  doc.setFontSize(36);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('times', 'bold');
  doc.text(report.grade, width / 2, coverCenterY + 44, { align: 'center' });
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.textLight);
  doc.setFont('helvetica', 'normal');
  doc.text("Overall Grade", width / 2, coverCenterY + 68, { align: 'center' });

  // Radar Chart Summary (Bottom of Cover)
  const radarY = coverCenterY + 90;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.textMain);
  doc.text("PERFORMANCE OVERVIEW", width / 2, radarY - 15, { align: 'center' });
  drawRadarChart(doc, report, width / 2, radarY + 25, 30);
  
  // Date & Prepared By (Bottom)
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.textLight);
  doc.setFont('helvetica', 'normal');
  const dateStr = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const preparedBy = report.whiteLabel?.agencyName ? `Prepared by ${report.whiteLabel.agencyName}` : "Prepared by BrandGenius Consultant Engine";
  doc.text(`${dateStr} | ${preparedBy}`, width / 2, height - 25, { align: 'center' });

  drawFooter(doc, 1, 7);

  // --- PAGE 2: EXECUTIVE SUMMARY ---
  doc.addPage();
  
  // Header
  doc.setFontSize(22);
  doc.setTextColor(...COLORS.textMain);
  doc.setFont('times', 'bold');
  doc.text("Executive Summary", margin, margin + 15);
  
  // Consultant Summary Box
  let y = margin + 30;
  doc.setFillColor(...COLORS.primaryLight);
  doc.roundedRect(margin, y, width - (margin * 2), 40, 3, 3, 'F');
  
  // Quote mark
  doc.setFontSize(40);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('times', 'bold');
  doc.text("“", margin + 5, y + 15);
  
  addWrappedText(doc, report.consultantSummary, margin + 15, y + 12, width - (margin * 2) - 20, 11, COLORS.textMain, 'italic', 'times');
  
  y += 55;
  
  // Custom Consultant Notes
  if (report.consultantNotes && report.consultantNotes.trim() !== '') {
      const boxMargin = margin;
      const innerMargin = 10;
      const boxWidth = width - (boxMargin * 2);
      
      // Calculate text height
      doc.setFontSize(11);
      const lines = doc.splitTextToSize(report.consultantNotes, boxWidth - (innerMargin * 2) - 5);
      const textHeight = lines.length * (11 * 0.5) + (lines.length * 2);
      const boxHeight = textHeight + 20;
      
      // Draw Highlight Box (Amber style for importance)
      doc.setFillColor(255, 251, 235); // amber-50
      doc.setDrawColor(245, 158, 11); // amber-500
      doc.setLineWidth(0.5);
      doc.roundedRect(boxMargin, y, boxWidth, boxHeight, 3, 3, 'FD');
      
      // Accent line on the left side of the box
      doc.setDrawColor(245, 158, 11); // amber-500
      doc.setLineWidth(2);
      doc.line(boxMargin + 3, y + 3, boxMargin + 3, y + boxHeight - 3);
      
      // Title
      doc.setFontSize(12);
      doc.setTextColor(217, 119, 6); // amber-600
      doc.setFont('helvetica', 'bold');
      doc.text("CONSULTANT NOTES", boxMargin + innerMargin + 5, y + 12);
      
      // Text
      addWrappedText(
          doc, 
          report.consultantNotes, 
          boxMargin + innerMargin + 5, 
          y + 22, 
          boxWidth - (innerMargin * 2) - 5, 
          11, 
          COLORS.textMain, 
          'italic', 
          'helvetica'
      );
      
      y += boxHeight + 15;
  }
  
  // Two Columns: Strengths & Risks
  const colWidth = (width - (margin * 2) - 10) / 2;
  
  // Key Strengths
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.success);
  doc.setFont('times', 'bold');
  doc.text("Key Strengths", margin, y);
  
  let strengthY = y + 10;
  const topStrengths = report.feedback.strengths.slice(0, 3);
  topStrengths.forEach(s => {
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.textMain);
      doc.setFont('helvetica', 'normal');
      doc.text(`✓ ${s}`, margin, strengthY);
      strengthY += 8;
  });
  
  // Critical Risks
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.danger);
  doc.setFont('times', 'bold');
  doc.text("Critical Risks", margin + colWidth + 10, y);
  
  let riskY = y + 10;
  const topRisks = report.feedback.risks.slice(0, 3);
  if (topRisks.length > 0) {
      topRisks.forEach(r => {
          doc.setFontSize(10);
          doc.setTextColor(...COLORS.textMain);
          doc.setFont('helvetica', 'normal');
          doc.text(`⚠ ${r}`, margin + colWidth + 10, riskY);
          riskY += 8;
      });
  } else {
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.textLight);
      doc.text("No critical risks identified.", margin + colWidth + 10, riskY);
  }
  
  y = Math.max(strengthY, riskY) + 20;
  
  // Strategic Action Plan
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('times', 'bold');
  doc.text("Strategic Action Plan", margin, y);
  doc.setDrawColor(...COLORS.primary);
  doc.line(margin, y + 2, width - margin, y + 2);
  
  y += 15;
  
  report.feedback.actions.forEach((action, idx) => {
      // Circle number
      doc.setFillColor(...COLORS.primary);
      doc.circle(margin + 4, y - 1.5, 4, 'F');
      doc.setTextColor(...COLORS.white);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`${idx + 1}`, margin + 4, y, { align: 'center', baseline: 'middle' });
      
      // Action Text
      doc.setTextColor(...COLORS.textMain);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(action, margin + 12, y);
      
      y += 12;
  });
  
  drawFooter(doc, 2, 7);

  // --- PAGES 3-7: CATEGORY DEEP DIVES ---
  const categories = ['linguistics', 'phonetics', 'digital', 'structural', 'strategic'];
  categories.forEach((catKey, index) => {
      // @ts-ignore
      const catData = report.categories[catKey];
      drawCategoryPage(doc, catKey, catData, width, height, margin);
      drawFooter(doc, index + 3, 7);
  });

  // --- SAVE ---
  const fileName = `${report.inputs.name}_BrandAudit_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
